import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Polygon, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { FaPlus } from "react-icons/fa";
import "./Fields.css";
import { UserContext } from "../../UserContext";
import axios from "axios";

const mockDataList = [
  {
    id: 1,
    name: "كوم حسن",
    area: 15,
    areaUnit: "Feddan",
    boundaries: [
      [31.2156, 29.9553],
      [31.2166, 29.9553],
      [31.2166, 29.9563],
      [31.2156, 29.9563],
    ],
  },
  {
    id: 2,
    name: "كوم عزيزة",
    area: 5,
    areaUnit: "Feddan",
    boundaries: [
      [31.2001, 29.9158],
      [31.2011, 29.9158],
      [31.2011, 29.9168],
      [31.2001, 29.9168],
    ],
  },
];

// --- Helper component to automatically fit the map to the given polygon bounds ---
const AutoFitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [10, 10] });
    }
  }, [bounds, map]);
  return null;
};

const FieldCard = ({ field }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const center = field.boundaries.reduce(
    (acc, curr) => [
      acc[0] + curr[0] / field.boundaries.length,
      acc[1] + curr[1] / field.boundaries.length,
    ],
    [0, 0]
  );

  const handleAddActivity = (e) => {
    e.stopPropagation();
    // Navigate to a new activity page or open a modal
    navigate(`/fields/${field.id}/tasks/new`);
  };

  return (
    <div className="field-card" onClick={() => navigate(`/fields/${field.id}`)}>
      <div className="card-map-container">
        <MapContainer
          center={center}
          scrollWheelZoom={false}
          dragging={false}
          doubleClickZoom={false}
          zoomControl={false}
          touchZoom={false}
          keyboard={false}
          boxZoom={false}
          attributionControl={false}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="&copy; Esri"
          />
          <Polygon
            positions={field.boundaries}
            pathOptions={{ color: "#c7356c", dashArray: "5, 5" }}
          />
          <AutoFitBounds bounds={field.boundaries} />
        </MapContainer>
      </div>
      <div className="card-details">
        <h3>{field.name}</h3>
        <p>{`${t("Area")}: ${field.area?.toFixed(2)} ${t(field.areaUnit)}`}</p>
      </div>
      {/* UPDATED Card Actions */}
      <div className="card-actions">
        <button className="button-add-activity" onClick={handleAddActivity}>
          <FaPlus /> {t("Add Activity")}
        </button>
      </div>
    </div>
  );
};

const FieldsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userData, token } = useContext(UserContext);

  // mock data integration
  // useEffect(() => {
  //   setLoading(true);
  //   setTimeout(() => {
  //     setFields(mockDataList);
  //     setLoading(false);
  //   }, 500);
  // }, []);

  const parseCustomLocationString = (locationString) => {
    if (!locationString || typeof locationString !== "string") {
      return [];
    }

    // This regex splits on a hyphen (-) only if it's preceded by a digit (\\d).
    // This correctly separates coordinate pairs without breaking negative numbers.
    const pairs = locationString.split(/(?<=\d)-/g);

    return pairs
      .map((pair) => pair.split(",").map(Number)) // Convert pairs to [lat, lng] arrays
      .filter(
        (
          coords // Filter out any invalid pairs as a safeguard
        ) =>
          Array.isArray(coords) &&
          coords.length === 2 &&
          !isNaN(coords[0]) &&
          !isNaN(coords[1])
      );
  };

  // backend integration
  useEffect(() => {
    const fetchFields = async () => {
      // 3. Check if user data and farmId are available
      if (!userData?.Farm?.id || !token) {
        setLoading(false);
        return;
      }

      const farmId = userData.Farm.id;

      try {
        // 4. Make the API call
        const response = await axios.get(`api/field/findByFarmId/${farmId}`, {
          headers: {
            Authorization: `${token}`,
          },
        });
        console.log("response", response);
        // 5. Process the response data
        const fetchedFields = response.data.items.map((field) => {
          // The API sends location as a string, but Leaflet needs a parsed array.
          // We add a 'boundaries' property here for the map component.
          try {
            field.boundaries = parseCustomLocationString(field.location);
            console.log(`${field.name} : \n ${field.boundaries}`);
          } catch (e) {
            console.error("Failed to parse field location:", field.location);
            field.boundaries = []; // Default to empty array on parse error
          }
          return field;
        });

        setFields(fetchedFields);
      } catch (error) {
        console.error("Failed to fetch fields:", error);
        // Optionally set an error state to show a message to the user
      } finally {
        setLoading(false);
      }
    };

    fetchFields();
  }, [userData, token]); // Re-run effect if userData or token changes

  if (loading)
    return <div className="page-message">{t("Loading Fields...")}</div>;

  return (
    <div className="fields-list-container">
      <header className="view-header">
        <div className="view-header-title-group">
          <h1>{t("Fields")}</h1>
          <p className="view-header-subtitle">
            {fields.length} {t("fields")}
          </p>
        </div>
        <div className="view-header-actions">
          <button
            className="add-new-button"
            onClick={() => navigate("/fields/new/map")}
          >
            <FaPlus /> {t("Add New Field")}
          </button>
        </div>
      </header>
      <div className="fields-grid">
        {fields.map((field) => (
          <FieldCard key={field.id} field={field} />
        ))}
      </div>
    </div>
  );
};

export default FieldsList;
